<template>
    <div class="card" v-if="learners">

        <div class="card-header" v-if="group">
            <div class="row">
                <div class="col">
                    <h3 class="mb-0 text-uppercase">
                        <a href="#" class="text-muted" @click.prevent="backToCohort">
                            {{ registration.cohort.name }}
                        </a> /
                        {{ group.name }}
                    </h3>
                </div>
            </div>
        </div>

        <p class="card-body mb-0 text-sm" v-if="empty">{{ $t('dashboard.no-learner') }}</p>

        <div :class="{ 'pt-3': !group }" v-if="!empty">
            
            <table-control :paging="paging" :search="true" v-model="search" v-if="!group"></table-control>

            <el-table class="table-responsive table-flush"
                    :show-header="false"
                    :data="filteredLearners">

                <el-table-column width="72">
                    <template v-slot="{row}">
                        <a href="#" class="avatar rounded-circle" :class="{'disabled': isWithdrawed(row)}"
                            @click.prevent="openOrEditLearner(row)"
                            v-if="row.session && (row.state.published || $permissions.hasRight('dashboard.learners.write'))"
                        >
                            <img :src="row.avatar">
                        </a>
                        <span class="avatar rounded-circle" :class="{'disabled': isWithdrawed(row)}" v-else>
                            <img :src="row.avatar">
                        </span>
                    </template>
                </el-table-column>

                <el-table-column>
                    <template v-slot="{row}">
                        <div :class="{'disabled': isWithdrawed(row)}">
                            <h4 class="mb-0">
                                <a href="#" @click.prevent="openOrEditLearner(row)"
                                    v-if="row.session && (row.state.published || $permissions.hasRight('dashboard.learners.write'))"
                                >
                                    {{row.name}}
                                </a>
                                <span v-else>
                                    {{row.name}}
                                </span>
                            </h4>
                            <small v-if="row.owner">
                                {{ row.owner.name }}
                            </small>
                            <small v-if="row.entity">
                                / {{ row.entity.name }}
                            </small>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column>
                    <template v-slot="{row}">

                        <badge type="success" v-if="row.state.published" class="mb-1">
                            <i class="fas fa-calendar-alt mr-1"></i>
                            {{ date(row) }}
                        </badge>

                        <small class="d-block"
                                v-if="row.state.published && row.state.meta && row.state.meta.go_ground">
                            <i class="fas fa-plane-departure mr-1" style="margin-left:14px"></i>
                            {{ $t('degrees.go-ground') }}
                        </small>

                        <small v-if="row.state.acknowledged" class="d-block">
                            <i class="fas fa-check mr-1"></i> {{ $t('dashboard.acknowledged') }}
                        </small>

                    </template>
                </el-table-column>

                <el-table-column align="right" class-name="actions" width="120">
                    <template v-slot="{row}">
                        <div class="p-1">

                            <base-button type="primary" size="sm" @click="openLearner(row)"
                                    v-if="row.state.published">
                                {{ $t('dashboard.more') }}
                            </base-button>

                            <base-button type="secondary" size="sm" @click="editLearner(row)"
                                v-if="(row.session || simulation.assessment) && !row.state.published && $permissions.hasRight('dashboard.learners.write')"
                            >
                                <i class="fas fa-pencil-alt"></i> {{ $t('common.edit') }}
                            </base-button>

                        </div>
                    </template>
                </el-table-column>
            </el-table>

            <table-pagination :paging="paging" v-if="!group"></table-pagination>                
        </div>

        <div class="card-footer text-center" v-if="group">
            <base-button type="secondary" size="sm" @click="backToCohort">
                {{ $t('dashboard.back-to-cohort') }}
            </base-button>
        </div>
    </div>
</template>

<script>
    import moment from 'moment'

    export default {

        props: {

            registration: {
                type: Object,
            },

            simulation: {
                type: Object,
            },

            learners: {
                type: Array,
            },

            group: {
                type: Object,
            }
        },

        data() {
            return {
                paging: {
                    currentPage: 1,
                    perPage: 10,
                    total: 0
                },
                search: '',
                moment: moment,
            }
        },

        computed: {

            empty() {
                return this.learners.length == 0 && !this.search
            },

            filteredLearners() {
                // Search filter.
                let learners = this.learners.filter(learner => !this.search
                    || learner.name.toLowerCase().includes(this.search.toLowerCase())
                )
                // Sorting.
                learners = learners.sort((a, b) => a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1)

                // Paging.
                if (this.group) {
                    return learners
                } else {
                    this.paging.total = learners.length
                    return learners.slice((this.paging.currentPage - 1) * this.paging.perPage, this.paging.currentPage * this.paging.perPage)
                }
            },
        },

        methods: {     

            isWithdrawed(row) {
                return !row.pivot.active
            },

            date(learner) {
                if (learner.session && learner.session.started_at) {
                    return this.moment(learner.session.started_at).format('YYYY/MM/DD')
                } else {
                    return this.moment(learner.state.date).format('YYYY/MM/DD')
                }
            },
            
            openOrEditLearner(learner) {
                if (learner.state.published) {
                    this.openLearner(learner)
                } else {
                    this.editLearner(learner)
                }
            },

            openLearner(learner) {
                this.$router.push({ name: 'dashboard-learner-simulation',
                    params: { 
                        learnerId: learner.id,
                        registrationId: this.registration.id,
                        simulationId: this.$route.params.simulationId,
                    }
                })
            },

            editLearner(learner) {
                if (this.simulation.assessment && this.simulation.private) {
                    this.$router.push({
                        name: 'dashboard-learner-private-review-report',
                        params: {
                            learnerId: learner.id,
                            registrationId: this.registration.id,
                            simulationId: this.$route.params.simulationId,
                        }
                    });
                } else {
                    this.$router.push({ name: 'dashboard-learner-simulation-edit',
                        params: { 
                            learnerId: learner.id,
                            registrationId: this.registration.id,
                            simulationId: this.$route.params.simulationId,
                        }
                    })
                }
            },

            backToCohort() {
                this.$router.push({ name: 'dashboard-cohort-simulation',
                    params: {
                        cohortId: this.registration.cohort.id,
                        registrationId: this.registration.id,
                        simulationId: this.$route.params.simulationId
                    }
                })
            }
        }
    }
</script>

<style scoped>

    .card >>> th,
    .card >>> td {
        padding-right: 0;
    }
    .card >>> th.actions,
    .card >>> td.actions {
        padding-right: 1.5rem;
    }
    .card >>> .table-control form {
        width: 100%;
    }
    .badge.badge-success {
        background-color: #1aae6f;
        color: #fff;
        font-size: 0.7rem;
    }
    .disabled, .disabled h4 {
        color: #bbb !important;
    }
    .disabled img {
        opacity: 0.5;
    }
    
</style>
